<template>
  <div>
    <div class="mb-3">
      <label for="document" class="form-label">
        Documento del Participante
      </label>
      <input
        type="text"
        class="form-control"
        id="document"
        v-model="localForm.document"
        placeholder="Ingrese el documento"
        required
      />
    </div>

    <div class="mb-3">
      <label for="event" class="form-label">Evento</label>
      <div class="position-relative">
        <input
          type="text"
          class="form-control"
          id="event"
          v-model="eventSearch"
          @input="filterEvents"
          @focus="showEventList = true"
          placeholder="Buscar evento..."
          autocomplete="off"
          required
        />
        <div
          v-if="showEventList && filteredEvents.length > 0"
          class="position-absolute w-100 bg-white border rounded shadow-sm"
          style="top: 100%; z-index: 1000; max-height: 200px; overflow-y: auto"
        >
          <div
            v-for="event in filteredEvents"
            :key="event.id"
            class="p-2 border-bottom cursor-pointer hover-bg-light"
            @click="selectEvent(event)"
          >
            <strong>{{ event.title }}</strong>
            <br />
            <small class="text-muted">{{ formatDate(event.start_date) }}</small>
          </div>
        </div>
      </div>
    </div>

    <div class="mb-3" v-if="localForm.selectedEvent">
      <button
        type="button"
        class="btn btn-outline-primary"
        @click="verifyEnrollment"
        :disabled="!localForm.document || verifying"
      >
        {{ verifying ? "Verificando..." : "Verificar Inscripción" }}
      </button>
    </div>

    <div v-if="enrollmentData" class="mb-3">
      <h6>Estado de Inscripción:</h6>
      <div v-if="enrollmentData.evn_enroll" class="text-success">
        <i class="fa fa-check"></i> Inscrito en evento
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import axios from "@/includes/connection";
import { toast } from "vue3-toastify";

const props = defineProps({
  formData: Object,
});

const emit = defineEmits(["dataVerified"]);

const verifying = ref(false);
const showEventList = ref(false);
const eventSearch = ref("");
const events = ref([]);
const enrollmentData = ref(null);

const localForm = ref({
  document: "",
  selectedEvent: null,
});

const filteredEvents = computed(() => {
  if (!eventSearch.value) return events.value;
  const search = eventSearch.value.toLowerCase();
  return events.value.filter((event) =>
    event.title.toLowerCase().includes(search)
  );
});

onMounted(() => {
  loadEvents();
});

async function loadEvents() {
  try {
    const response = await axios.get("/event/support");
    if (response.data) {
      events.value = response.data;
    }
  } catch (error) {
    console.error("Error", error);
    toast.error("Error al cargar eventos");
  }
}

function filterEvents() {
  showEventList.value = true;
}

function selectEvent(event) {
  localForm.value.selectedEvent = event;
  eventSearch.value = `${event.title} - ${formatDate(event.start_date)}`;
  showEventList.value = false;
  enrollmentData.value = null;
}

async function verifyEnrollment() {
  if (!localForm.value.document || !localForm.value.selectedEvent) {
    toast.warning("Complete documento y evento primero");
    return;
  }

  verifying.value = true;

  try {
    const response = await axios.post("/event-enrollment/verify", {
      document: localForm.value.document,
      id: localForm.value.selectedEvent.id,
    });

    enrollmentData.value = response.data;

    if (!response.data.evn_enroll) {
      toast.warning("No se encontró inscripción para este evento");
    } else {
      toast.success("Inscripción verificada correctamente");

      emit("dataVerified", {
        participant_document: localForm.value.document,
        event_id: localForm.value.selectedEvent.id,
        event_enrollment_id: response.data.evn_enroll,
        enrollment_base_id: response.data.en_base,
        attendance_id: response.data.attend,
        detail: `Solicitud de comprobante de inscripción del participante ${localForm.value.document} para el evento "${localForm.value.selectedEvent.title}"`,
      });
    }
  } catch (error) {
    console.error("Error", error);
    toast.error("Error al verificar inscripción");
    enrollmentData.value = null;
  } finally {
    verifying.value = false;
  }
}

function formatDate(dateString) {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("es-ES", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.hover-bg-light:hover {
  background-color: #f8f9fa;
}
</style>
